import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useKeyDown } from "@react-hooks-library/core";
import { AnimatePresence, m } from "framer-motion";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { twJoin, twMerge } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";
import { AgentVersionConfigurationInputToolsInner } from "src/clients/agent-management";

/** Animation duration for drawer enter/exit transitions in milliseconds */
export const SIDE_DRAWER_ANIMATION_DURATION_MS = 150;

// ============================================================================
// Drawer Types - Discriminated Union
// ============================================================================

/** Data for the flow tool configuration drawer */
export type FlowToolConfigDrawerData = {
  tool: AgentVersionConfigurationInputToolsInner;
  toolIndex: number;
  onToolChange: (tool: AgentVersionConfigurationInputToolsInner) => void;
  onRequestChangeFlow: () => void;
  immutable: boolean;
};

/** Data for the add tool drawer */
export type AddToolDrawerData = {
  onAddFlowTool: () => void;
};

/**
 * Discriminated union for open drawer state.
 * Each drawer type has its own variant with typed data.
 * Use type narrowing: `if (openDrawer?.type === "flowToolConfig") { openDrawer.data.tool... }`
 */
export type OpenDrawerState =
  | { type: "flowToolConfig"; data: FlowToolConfigDrawerData }
  | { type: "addTool"; data: AddToolDrawerData }
  | { type: "playground" }
  | { type: "jsonSchema" };

/** All possible drawer type values */
export type DrawerType = OpenDrawerState["type"];

// ============================================================================
// Coordinator Context + Provider
// ============================================================================

/** Animation mode for drawer transitions */
type TransitionMode = "slide" | "crossfade";

type SideDrawerCoordinatorContextProps = {
  /** Currently open drawer state (null if closed) */
  openDrawer: OpenDrawerState | null;
  /** Drawer ID that is currently animating out */
  exitingDrawerId: DrawerType | null;
  /** Current transition animation mode */
  transitionMode: TransitionMode;
  /** Request to open a drawer - type-safe with discriminated union */
  requestOpen: (state: OpenDrawerState) => void;
  /** Request to close current drawer */
  requestClose: () => void;
  /** Specific drawer calls this when close succeeds */
  notifyCloseAccepted: () => void;
  /** Specific drawer calls this when user rejects close (e.g. "Keep editing") */
  notifyCloseRejected: () => void;
  /** Register a close handler for a drawer (internal use by useSideDrawer) */
  registerHandler: (drawerType: DrawerType, handler: () => void) => () => void;
};

const SideDrawerCoordinatorContext =
  createContext<SideDrawerCoordinatorContextProps | null>(null);

/**
 * Central coordinator for the side drawer system.
 * Manages open/close requests and transitions between drawers.
 */
export const SideDrawerCoordinatorProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Single state for open drawer - discriminated union ensures type and data stay in sync
  const [openDrawer, setOpenDrawer] = useState<OpenDrawerState | null>(null);

  // Use ref for pending drawer to avoid stale closure issues
  // The handler is called synchronously in the same render cycle,
  // so useCallback wouldn't have access to the new value yet
  const pendingDrawerRef = useRef<OpenDrawerState | null>(null);

  // Transition state for animation coordination
  const [exitingDrawerId, setExitingDrawerId] = useState<DrawerType | null>(
    null,
  );
  const transitionModeRef = useRef<TransitionMode>("slide");
  const exitingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (exitingTimeoutRef.current) {
        clearTimeout(exitingTimeoutRef.current);
      }
    };
  }, []);

  // Store close handlers for each drawer
  const handlersRef = useRef<Map<DrawerType, () => void>>(new Map());

  const registerHandler = useCallback(
    (drawerType: DrawerType, handler: () => void) => {
      handlersRef.current.set(drawerType, handler);
      return () => {
        handlersRef.current.delete(drawerType);
      };
    },
    [],
  );

  const notifyCloseAccepted = useCallback(() => {
    const pending = pendingDrawerRef.current;

    if (pending) {
      // Switching drawers → true crossfade (simultaneous fade out/in)
      transitionModeRef.current = "crossfade";
      setExitingDrawerId(openDrawer?.type ?? null); // Mark current drawer as exiting
      setOpenDrawer(pending); // Open new drawer immediately with its data
      pendingDrawerRef.current = null;

      // Cancel any previous exiting timeout to prevent stale callbacks
      if (exitingTimeoutRef.current) {
        clearTimeout(exitingTimeoutRef.current);
      }

      // Clear exiting drawer after animation completes
      exitingTimeoutRef.current = setTimeout(() => {
        setExitingDrawerId(null);
        exitingTimeoutRef.current = null;
      }, SIDE_DRAWER_ANIMATION_DURATION_MS);
    } else {
      // Closing to empty → slide animation
      transitionModeRef.current = "slide";
      setOpenDrawer(null);
    }
  }, [openDrawer?.type]);

  const notifyCloseRejected = useCallback(() => {
    pendingDrawerRef.current = null;
  }, []);

  const requestOpen = useCallback(
    (state: OpenDrawerState) => {
      const currentType = openDrawer?.type;

      if (currentType && currentType !== state.type) {
        // Another drawer is open, store pending and call its close handler
        // Animation mode will be set to "crossfade" in notifyCloseAccepted
        pendingDrawerRef.current = state;
        const handler = handlersRef.current.get(currentType);
        if (handler) {
          handler();
        } else {
          // No handler registered for the open drawer - auto-accept close
          notifyCloseAccepted();
        }
      } else if (!currentType) {
        // No drawer open, open immediately with slide animation
        transitionModeRef.current = "slide";
        setOpenDrawer(state);
      } else {
        // If same drawer requested just refresh the
        // state as the data may have changed
        setOpenDrawer(state);
      }
    },
    [openDrawer?.type, notifyCloseAccepted],
  );

  const requestClose = useCallback(() => {
    const currentType = openDrawer?.type;
    if (!currentType) {
      return;
    }
    // transitionMode will be set to "slide" in notifyCloseAccepted
    // Direct close (not switching) → will use slide animation
    const handler = handlersRef.current.get(currentType);
    if (handler) {
      handler();
    } else {
      // No handler registered - auto-accept close
      notifyCloseAccepted();
    }
  }, [openDrawer?.type, notifyCloseAccepted]);

  return (
    <SideDrawerCoordinatorContext.Provider
      value={{
        openDrawer,
        exitingDrawerId,
        transitionMode: transitionModeRef.current,
        requestOpen,
        requestClose,
        notifyCloseAccepted,
        notifyCloseRejected,
        registerHandler,
      }}
    >
      {children}
    </SideDrawerCoordinatorContext.Provider>
  );
};

export const useSideDrawerCoordinator =
  (): SideDrawerCoordinatorContextProps => {
    const context = useContext(SideDrawerCoordinatorContext);
    if (!context) {
      throw new Error(
        "useSideDrawerCoordinator must be used within a SideDrawerCoordinatorProvider",
      );
    }
    return context;
  };

// ============================================================================
// useSideDrawer hook (for drawer components to interact with the coordinator)
// ============================================================================

type UseSideDrawerOptions = {
  /**
   * Called when coordinator requests this drawer to close.
   * Receives accept/reject callbacks as arguments to avoid closure issues.
   */
  onCloseRequested?: (accept: () => void, reject: () => void) => void;
};

// Type maneuvers so the open function is type safe

type DrawerStateByType<T extends DrawerType> = Extract<
  OpenDrawerState,
  { type: T }
>;

type DrawerOpenArgs<T extends DrawerType> =
  DrawerStateByType<T> extends { data: infer D } ? [data: D] : [];

type UseSideDrawerResult<T extends DrawerType> = {
  /** Whether this drawer is currently open */
  isOpen: boolean;
  /** Open this drawer */
  open: (...args: DrawerOpenArgs<T>) => void;
  /** Notify coordinator that close was accepted */
  notifyCloseAccepted: () => void;
  /** Notify coordinator that close was rejected (e.g., user clicked "Keep editing") */
  notifyCloseRejected: () => void;
};

/**
 * Hook for drawer components to interact with the coordinator.
 */
export const useSideDrawer = <T extends DrawerType>(
  drawerId: T,
  options?: UseSideDrawerOptions,
): UseSideDrawerResult<T> => {
  const {
    openDrawer,
    notifyCloseAccepted,
    notifyCloseRejected,
    registerHandler,
    requestOpen,
  } = useSideDrawerCoordinator();

  const isOpen = openDrawer?.type === drawerId;

  // Register the close handler - only if onCloseRequested is provided
  // This prevents components that only use open() from overwriting handlers
  // that have actual close logic
  const onCloseRequestedRef = useRef(options?.onCloseRequested);
  onCloseRequestedRef.current = options?.onCloseRequested;

  useEffect(() => {
    // Only register if we have an onCloseRequested callback
    // Components that just want open() shouldn't register a handler
    if (!options?.onCloseRequested) {
      return;
    }

    const handler = () => {
      if (onCloseRequestedRef.current) {
        onCloseRequestedRef.current(notifyCloseAccepted, notifyCloseRejected);
      } else {
        // Shouldn't reach here since we only register when onCloseRequested exists
        notifyCloseAccepted();
      }
    };
    return registerHandler(drawerId, handler);
  }, [
    drawerId,
    registerHandler,
    notifyCloseAccepted,
    notifyCloseRejected,
    options?.onCloseRequested,
  ]);

  const open = useCallback(
    (...args: DrawerOpenArgs<T>) => {
      const state =
        args.length > 0
          ? ({ type: drawerId, data: args[0] } as DrawerStateByType<T>)
          : ({ type: drawerId } as DrawerStateByType<T>);

      requestOpen(state);
    },
    [drawerId, requestOpen],
  );

  return {
    isOpen,
    open,
    notifyCloseAccepted,
    notifyCloseRejected,
  };
};

// ============================================================================
// SideDrawer Component
// ============================================================================

type SideDrawerProps = {
  children: React.ReactNode;
  /** Type identifier for this drawer (used by coordinator) */
  drawerId: DrawerType;
  width?: "default" | "wide";
  dataLoc?: string;
  /** Header configuration - if provided, renders a standard header */
  header?: SideDrawerHeaderConfig;
};

type SideDrawerHeaderConfig = {
  title: string;
  /** Content rendered immediately after the title (e.g., status indicator) */
  titleSuffix?: React.ReactNode;
  description?: string;
  /** Extra content rendered to the left of the close button */
  extraActions?: React.ReactNode;
  closeDataLoc?: string;
  reverseTitleAndDescription?: boolean;
  icon?: React.ReactNode;
};

const WIDTH_CLASSES = {
  default: "w-[720px] min-w-[464px] max-w-[40vw]",
  wide: "w-[800px] min-w-[600px] max-w-[40vw]",
} as const;

// shadow-lg dimensions with reduced opacity
const SUBTLE_SHADOW =
  "0 10px 15px -3px rgba(0,0,0,0.07), 0 4px 6px -4px rgba(0,0,0,0.07)";

// Animation variants for drawer transitions
const SLIDE_OFFSET = 20; // pixels to slide in/out (subtle movement)

const slideVariants = {
  initial: { opacity: 0, x: SLIDE_OFFSET },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: SLIDE_OFFSET },
};

const fadeVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const SideDrawer: React.FC<SideDrawerProps> = ({
  children,
  drawerId,
  width = "default",
  dataLoc,
  header,
}) => {
  const { openDrawer, exitingDrawerId, transitionMode, requestClose } =
    useSideDrawerCoordinator();

  const isOpen = openDrawer?.type === drawerId;
  const isExiting = exitingDrawerId === drawerId;
  const shouldRender = isOpen || isExiting;

  // Handle Escape key to dismiss drawer
  useKeyDown(["Escape"], (e) => {
    if (!isOpen) return;
    e.preventDefault();
    requestClose();
  });

  // Use slide animation for opening/closing from empty state
  // Use fade animation for switching between drawers
  const variants = transitionMode === "slide" ? slideVariants : fadeVariants;

  return (
    <AnimatePresence>
      {shouldRender && (
        <m.div
          animate={isExiting ? "exit" : "animate"}
          className={twMerge(
            WIDTH_CLASSES[width],
            "h-full flex-shrink-0 border-l border-gray-200 bg-white",
            header && "flex flex-col",
            // Absolute positioning when exiting so it doesn't affect layout
            isExiting && "absolute bottom-0 right-0 top-0 z-10",
          )}
          data-loc={dataLoc}
          exit="exit"
          initial={isExiting ? undefined : "initial"}
          style={{ boxShadow: SUBTLE_SHADOW }}
          transition={{
            duration: SIDE_DRAWER_ANIMATION_DURATION_MS / 1000,
            ease: "easeOut",
          }}
          variants={variants}
        >
          {/* Drawer Header */}
          {header && (
            <div className="flex-shrink-0 px-5 pt-4">
              <div className="flex items-start justify-between border-b border-gray-100 pb-4">
                <div className="flex items-center gap-2">
                  {header.icon}
                  <div
                    className={twJoin(
                      header.reverseTitleAndDescription &&
                        "flex flex-col-reverse",
                    )}
                  >
                    <h3 className="flex items-center gap-1.5 text-gray-800 font-inter-semibold-13px">
                      {header.title}
                      {header.titleSuffix}
                    </h3>
                    {header.description && (
                      <p className="mt-1 text-gray-500 font-inter-normal-12px">
                        {header.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {header.extraActions}
                  <Icon
                    color="text-gray-500 hover:text-gray-700 cursor-pointer"
                    dataLoc={header.closeDataLoc}
                    icon={faTimes}
                    size="xs"
                    onClick={requestClose}
                  />
                </div>
              </div>
            </div>
          )}
          {/* Inner content */}
          {header ? <div className="min-h-0 flex-1">{children}</div> : children}
        </m.div>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// DrawerContainer Component
// ============================================================================

type DrawerContainerProps = {
  children: React.ReactNode;
};

/**
 * Wrapper component for drawer content that provides positioning context.
 * Place all SideDrawer components inside this wrapper for proper crossfade transitions.
 */
export const DrawerContainer: React.FC<DrawerContainerProps> = ({
  children,
}) => {
  // Relative positioning provides context for absolutely positioned exiting drawers
  return <div className="relative">{children}</div>;
};
